import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { mapApiLeaveBalanceToLeaveBalance } from '../utils/apiMappers';
import type { LeaveBalance } from '../types/frontendTypes';
import { Card, CardHeader, CardTitle, CardContent } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Alert } from '../components/common/Alert';
import { Badge } from '../components/common/Badge';
import { useNotification } from '../contexts/NotificationContext';

interface LeaveRequestForm {
  leaveTypeId: string;
  startDate: string;
  endDate: string;
  reason: string;
  emergencyContact?: string;
  emergencyPhone?: string;
  handoverNotes?: string;
  substituteId?: string;
  areaOfResponsibility?: string;
}

export const NewLeaveRequest = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { t } = useTranslation();
  const [workingDays, setWorkingDays] = useState(0);
  
  const { register, handleSubmit, watch, formState: { errors } } = useForm<LeaveRequestForm>();
  
  // Watch for date changes
  const startDate = watch('startDate');
  const endDate = watch('endDate');
  
  // Fetch leave types
  const { data: leaveTypes, isError: leaveTypesError, isLoading: leaveTypesLoading } = useQuery({
    queryKey: ['leaveTypes'],
    queryFn: async () => {
      try {
        const response = await api.get('/leaves/types/all');
        console.log('Leave types response:', response.data);
        return response.data || [];
      } catch (error) {
        console.error('Leave types loading error:', error);
        // Fallback: try alternative endpoint
        try {
          const fallbackResponse = await api.get('/leave-types');
          console.log('Leave types fallback response:', fallbackResponse.data);
          return fallbackResponse.data?.leaveTypes || fallbackResponse.data || [];
        } catch (fallbackError) {
          console.error('Leave types fallback error:', fallbackError);
          throw error;
        }
      }
    },
    retry: 2
  });
  
  // Fetch leave balances
  const { data: rawBalances } = useQuery({
    queryKey: ['leaveBalances'],
    queryFn: async () => {
      const response = await api.get('/leaves/balances');
      return response.data || [];
    }
  });
  
  // Map balances using proper mapper
  const balances: LeaveBalance[] = rawBalances?.map((balance: any) => mapApiLeaveBalanceToLeaveBalance(balance)) || [];

  // Fetch users for substitute selection
  const { data: users, isError: usersError, isLoading: usersLoading } = useQuery({
    queryKey: ['users-for-substitution'],
    queryFn: async () => {
      try {
        const response = await api.get('/users/for-substitution');
        console.log('Users for substitution response:', response.data);
        return response.data.users || [];
      } catch (error) {
        console.error('Users for substitution loading error:', error);
        throw error;
      }
    },
    retry: 2
  });

  // Calculate working days when dates change (excluding weekends)
  useEffect(() => {
    if (startDate && endDate) {
      const start = parseISO(startDate);
      const end = parseISO(endDate);
      
      // Calculate actual business days (Mon-Fri only)
      let workingDaysCount = 0;
      let currentDate = new Date(start);
      
      while (currentDate <= end) {
        const dayOfWeek = currentDate.getDay();
        // 0 = Sunday, 6 = Saturday - exclude weekends
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          workingDaysCount++;
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      setWorkingDays(Math.max(0, workingDaysCount));
    }
  }, [startDate, endDate]);

  const createRequestMutation = useMutation({
    mutationFn: async ({ data, isDraft }: { data: LeaveRequestForm; isDraft: boolean }) => {
      console.log('Creating leave request:', data, 'isDraft:', isDraft);
      
      try {
        // Create leave request
        const response = await api.post('/leaves', {
          leave_type_id: data.leaveTypeId,
          start_date: data.startDate,
          end_date: data.endDate,
          reason: data.reason,
          emergency_contact: data.emergencyContact,
          emergency_phone: data.emergencyPhone,
          handover_notes: data.handoverNotes,
          status: isDraft ? 'draft' : 'pending'
        });
        
        console.log('Leave request created successfully:', response.data);
        
        // If substitute is selected, create substitution request
        if (data.substituteId && response.data?.id) {
          try {
            await api.post(`/leaves/${response.data.id}/substitute`, {
              substitute_id: data.substituteId,
              area_of_responsibility: data.areaOfResponsibility,
              handover_notes: data.handoverNotes
            });
            console.log('Substitution request created successfully');
          } catch (substitutionError) {
            console.warn('Substitution request failed, but main request succeeded:', substitutionError);
            // Don't throw error here - main request succeeded
          }
        }
        
        return response.data;
      } catch (error) {
        console.error('Leave request creation failed:', error);
        throw error;
      }
    },
    onSuccess: (data, variables) => {
      console.log('Mutation success callback triggered:', data);
      showNotification({
        type: 'success',
        title: variables.isDraft ? t('newRequest.draftSaved', 'Draft Saved') : t('newRequest.created', 'Leave Request Created'),
        message: variables.isDraft ? 
          t('newRequest.draftSavedMessage', 'Your leave request has been saved as draft.') : 
          t('newRequest.success')
      });
      navigate('/leaves');
    },
    onError: (error: any) => {
      console.error('Mutation error callback triggered:', error);
      showNotification({
        type: 'error',
        title: t('newRequest.errorTitle', 'Failed to Create Leave Request'),
        message: error.response?.data?.error || error.message || t('newRequest.error')
      });
    },
    // Zabezpieczenie przed wielokrotnym wywołaniem
    mutationKey: ['createLeaveRequest']
  });

  const onSubmit = (data: LeaveRequestForm, isDraft: boolean = false) => {
    // Zapobiegaj wielokrotnemu wysłaniu
    if (createRequestMutation.isPending) {
      console.log('Request already in progress');
      return;
    }
    createRequestMutation.mutate({ data, isDraft });
  };

  const selectedLeaveType = watch('leaveTypeId');
  const balance = balances?.find((b) => b.leaveTypeId === Number(selectedLeaveType));

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">{t('newRequest.title')}</h1>
        <p className="mt-1 text-sm text-gray-500">
          {t('newRequest.subtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>{t('newRequest.requestDetails')}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit((data) => onSubmit(data, false))} className="space-y-6">
                {createRequestMutation.isError && (
                  <Alert variant="error">
                    {t('newRequest.error')}
                  </Alert>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('newRequest.leaveType')}
                  </label>
                  <select
                    {...register('leaveTypeId', { required: t('newRequest.leaveTypeRequired') })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    disabled={leaveTypesLoading}
                  >
                    <option value="">
                      {leaveTypesLoading ? t('common.loading') : t('newRequest.selectLeaveType')}
                    </option>
                    {leaveTypes?.map((type: any) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                  {leaveTypesError && (
                    <p className="mt-1 text-sm text-red-600">{t('newRequest.leaveTypesLoadError', 'Failed to load leave types')}</p>
                  )}
                  {errors.leaveTypeId && (
                    <p className="mt-1 text-sm text-red-600">{errors.leaveTypeId.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    type="date"
                    label={t('newRequest.startDate')}
                    {...register('startDate', { required: t('newRequest.startDateRequired') })}
                    error={errors.startDate?.message}
                  />
                  <Input
                    type="date"
                    label={t('newRequest.endDate')}
                    {...register('endDate', { 
                      required: t('newRequest.endDateRequired'),
                      validate: (value) => {
                        if (startDate && value < startDate) {
                          return t('newRequest.endDateValidation');
                        }
                        return true;
                      }
                    })}
                    error={errors.endDate?.message}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('newRequest.reason')}
                  </label>
                  <textarea
                    {...register('reason', { required: t('newRequest.reasonRequired') })}
                    rows={3}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder={t('newRequest.reasonPlaceholder')}
                  />
                  {errors.reason && (
                    <p className="mt-1 text-sm text-red-600">{errors.reason.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label={t('newRequest.emergencyContact')}
                    {...register('emergencyContact')}
                    placeholder={t('newRequest.emergencyContactPlaceholder')}
                  />
                  <Input
                    label={t('newRequest.emergencyPhone')}
                    {...register('emergencyPhone')}
                    placeholder={t('newRequest.emergencyPhonePlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('newRequest.substitute')}
                  </label>
                  <select
                    {...register('substituteId')}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    disabled={usersLoading}
                  >
                    <option value="">
                      {usersLoading ? t('common.loading') : t('newRequest.selectSubstitute')}
                    </option>
                    {users?.map((user: any) => (
                      <option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name} - {user.position || 'Employee'}
                      </option>
                    ))}
                  </select>
                  {usersError && (
                    <p className="mt-1 text-sm text-red-600">{t('newRequest.usersLoadError', 'Failed to load users')}</p>
                  )}
                </div>

                {watch('substituteId') && (
                  <div>
                    <Input
                      label={t('newRequest.areaOfResponsibility')}
                      {...register('areaOfResponsibility')}
                      placeholder={t('newRequest.areaPlaceholder')}
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('newRequest.handoverNotes')}
                  </label>
                  <textarea
                    {...register('handoverNotes')}
                    rows={3}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder={t('newRequest.handoverPlaceholder')}
                  />
                </div>

                <div className="flex justify-end gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/leaves')}
                  >
                    {t('newRequest.cancel')}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => handleSubmit((data) => onSubmit(data, true))()}
                    isLoading={createRequestMutation.isPending}
                    disabled={createRequestMutation.isPending}
                  >
                    {t('newRequest.saveAsDraft')}
                  </Button>
                  <Button
                    type="submit"
                    isLoading={createRequestMutation.isPending}
                    disabled={createRequestMutation.isPending}
                  >
                    {t('newRequest.submitRequest')}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-1">
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>{t('newRequest.leaveSummary')}</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t('newRequest.workingDays')}</dt>
                  <dd className="mt-1 text-2xl font-semibold text-gray-900">
                    {workingDays} {t('newRequest.days')}
                  </dd>
                </div>
                {balance && (
                  <>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{t('newRequest.currentBalance')}</dt>
                      <dd className="mt-1 text-lg font-medium text-gray-900">
                        {balance.remaining} {t('newRequest.days')}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{t('newRequest.afterApproval')}</dt>
                      <dd className="mt-1 text-lg font-medium text-gray-900">
                        {Math.max(0, balance.remaining - workingDays)} {t('newRequest.days')}
                      </dd>
                    </div>
                  </>
                )}
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('newRequest.yourBalances')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {balances?.map((balance) => (
                  <div key={balance.id} className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {balance.leaveType?.name || t('newRequest.unknownLeaveType')}
                      </p>
                      <p className="text-xs text-gray-500">
                        {t('newRequest.used')}: {balance.used} / {balance.initialBalance}
                      </p>
                    </div>
                    <Badge variant="primary">
                      {balance.remaining} {t('newRequest.days')}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};